// ---------------------------------------------------------------------------------------------------------------------
// MP CUSTOM ELEMENTS
// ---------------------------------------------------------------------------------------------------------------------
// Custom web components migrated from the Mobile Pixels theme to back the `mp-figure`, `mp-links` and `mp-product-list`
// sections. Kept in a standalone module so the bundled `theme.js` is never touched (survives theme upgrades).
// Dependencies are resolved through the theme's importmap (see layout/theme.liquid): "vendor" (Motion) and "luxon".
//
//  * countdown-timer  - used by the `mp-figure-timer` snippet (depends on luxon for timezone-aware parsing)
//  * mp-product-list  - scroll-reveal animation for the `mp-product-list` section (depends on Motion from vendor)
//  * mp-countdown     - banner countdown bar for the `mp-countdown` section (depends on luxon, LA-timezone based)
//
// Note: `<mp-figure>` is purely CSS-styled in the source theme (its define is commented out), so it needs no JS here.

import { inView, timeline } from "vendor";
import { DateTime } from "luxon";

window.DateTime = DateTime;

var CountdownTimer = class extends HTMLElement {
  constructor() {
    super();
    this._interval = null;
    this._text = this.querySelector('template').content.querySelector('countdown-text').innerHTML;
    this._end = this.getAttribute("end") ? DateTime.fromFormat(this.getAttribute("end") + " America/Los_Angeles", 'yyyy-MM-dd HH:mm:ss z').toJSDate() : null;
    this._show = this.getAttribute("show") ? DateTime.fromFormat(this.getAttribute("show") + " America/Los_Angeles", 'yyyy-MM-dd HH:mm:ss z').toJSDate() : null;
  }
  connectedCallback() {
    if (!this._isValidDate(this._end) || !this._text) {
      return this.disconnectedCallback();
    }

    if (this._isValidDate(this._show) && new Date() < this._show) {
      return this.disconnectedCallback();
    }
    this._interval = setInterval(this._update.bind(this), 1e3);
    this._update();
  }
  _update() {
    const now = new Date();
    if (now >= this._end) {
      if (this.parentNode.parentNode.classList.contains('offer')) {
        this.parentNode.parentNode.remove();
      } else {
        this.parentNode.remove();
      }
      this.disconnectedCallback();
    } else {
      this.innerHTML = this._text.replace('{$TIME}', this._formatDiff(this._end - now));
    }
  }
  _isValidDate(d) {
    return d instanceof Date && !isNaN(d.getTime());
  }
  _formatDiff(diff) {
    const formatNumber = (num) => {
      return num < 10 ? `0${num}` : num;
    };
    const days = formatNumber(Math.floor(diff / (1000 * 60 * 60 * 24)));
    const hours = formatNumber(Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
    const minutes = formatNumber(Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)));
    const seconds = formatNumber(Math.floor((diff % (1000 * 60)) / 1000));
    return `${days}<sub>D</sub> : ${hours}<sub>H</sub> : ${minutes}<sub>M</sub> : ${seconds}<sub>S</sub>`;
  }
  disconnectedCallback() {
    this._interval && clearInterval(this._interval);
  }
};

if (!window.customElements.get("countdown-timer")) {
  window.customElements.define("countdown-timer", CountdownTimer);
}

var MPProductList = class extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      inView(this, this._reveal.bind(this), { margin: "-100px" });
    }
  }
  _reveal() {
    const toReveal = Array.from(this.querySelectorAll("[reveal-js]"));
    timeline([
      [toReveal, { opacity: 1 }, { duration: 0 }],
      [toReveal.map((item) => item.querySelector("img, svg")), { opacity: [0, 1], transform: ["scale(1.03)", "auto"] }, { duration: 0.2 }],
      [toReveal.map((item) => item.querySelector(".mp-product-card__info")), { opacity: [0, 1] }, { duration: 0.2 }]
    ]);
  }
};

if (!window.customElements.get("mp-product-list")) {
  window.customElements.define("mp-product-list", MPProductList);
}

// ---------------------------------------------------------------------------------------------------------------------
// MP COUNTDOWN - banner countdown bar (`mp-countdown` section)
// ---------------------------------------------------------------------------------------------------------------------
// Shows a Days/Hrs/Mins/Secs bar between `show` and `end` datetimes (both interpreted in America/Los_Angeles). Hides the
// days slot once it hits zero, removes itself when the countdown ends, and remembers a manual close for the session.
var MPCountdown = class extends HTMLElement {
  constructor() {
    super();
    if (window.sessionStorage.getItem('mpCountdownClosed') === 'true') {
      this.remove();
      return;
    }
    this._interval = null;
    this._end = this.getAttribute("end") ? DateTime.fromFormat(this.getAttribute("end") + " America/Los_Angeles", 'yyyy-MM-dd HH:mm:ss z').toJSDate() : null;
    this._show = this.getAttribute("show") ? DateTime.fromFormat(this.getAttribute("show") + " America/Los_Angeles", 'yyyy-MM-dd HH:mm:ss z').toJSDate() : null;

    this._close = this.querySelector('.close-btn');
    this._close.addEventListener("click", this._onCloseClicked.bind(this));

    this._days = this.querySelector('.days');
    this._daysSem = this.querySelector('.days-sem');
    this._hours = this.querySelector('.hours');
    this._minutes = this.querySelector('.minutes');
    this._seconds = this.querySelector('.seconds');
  }
  connectedCallback() {
    if (!this._isValidDate(this._end)) {
      this.disconnectedCallback();
      return;
    }

    if (this._isValidDate(this._show) && this._now() < this._show) {
      this.disconnectedCallback();
      return;
    }
    this._interval = setInterval(this._update.bind(this), 1e3);
    this._update();
  }
  _onCloseClicked() {
    this.style.height = '0';
    this.addEventListener('transitionend', function() {
      this.disconnectedCallback();
      window.sessionStorage.setItem('mpCountdownClosed', 'true');
    }, { once: true });
  }
  _now() {
    return DateTime.now().setZone('America/Los_Angeles').toJSDate();
  }
  _update() {
    const now = this._now();
    if (now >= this._end) {
      this.disconnectedCallback();
    } else {
      const [days, hours, minutes, seconds] = this._formatDiff(this._end - now);
      if (days === '00') {
        this._days.parentElement.style.display = 'none';
        this._daysSem.style.display = 'none';
      } else {
        this._days.innerHTML = days;
      }
      this._hours.innerHTML = hours;
      this._minutes.innerHTML = minutes;
      this._seconds.innerHTML = seconds;
    }
  }
  _isValidDate(d) {
    return d instanceof Date && !isNaN(d.getTime());
  }
  _formatDiff(diff) {
    const formatNumber = (num) => {
      return num < 10 ? `0${num}` : num;
    };
    const days = formatNumber(Math.floor(diff / (1000 * 60 * 60 * 24)));
    const hours = formatNumber(Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
    const minutes = formatNumber(Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)));
    const seconds = formatNumber(Math.floor((diff % (1000 * 60)) / 1000));
    return [days, hours, minutes, seconds];
  }
  disconnectedCallback() {
    this._interval && clearInterval(this._interval);
    this.remove();
  }
};

if (!window.customElements.get("mp-countdown")) {
  window.customElements.define("mp-countdown", MPCountdown);
}

// ---------------------------------------------------------------------------------------------------------------------
// AP NEWSLETTER POPUP - centered activity/promo modal with countdown + re-open teaser (`ap-newsletter-popup` section)
// ---------------------------------------------------------------------------------------------------------------------
// Opens `apparition-delay` seconds after load, at most once every `frequency-days` days per visitor (localStorage
// timestamp), only inside the [show, end] active window (America/Los_Angeles, like mp-countdown). The countdown targets
// the Countdown block's own `data-countdown-end` (independent of the active window) and does not close the popup when it
// hits zero. Closing (X / overlay / Esc / a data-apnp-close button) reveals the sibling Teaser tab (referenced by the
// `teaser` attribute), which re-opens the popup on click and can be dismissed for the session. Focus is trapped while
// open and restored on close. Previews in the theme editor (Shopify.designMode) ignore the frequency cap and window.
var APNewsletterPopup = class extends HTMLElement {
  constructor() {
    super();
    this._interval = null;
    this._openTimer = null;
    this._opened = false;

    this._delay = parseInt(this.getAttribute("apparition-delay") || "0", 10);
    this._freqDays = parseInt(this.getAttribute("frequency-days") || "0", 10);
    this._key = "ap:newsletter-popup:" + (this.getAttribute("campaign-key") || "default");
    this._teaserDismissKey = this._key + ":teaser-closed";
    this._end = this.getAttribute("end") ? DateTime.fromFormat(this.getAttribute("end") + " America/Los_Angeles", 'yyyy-MM-dd HH:mm:ss z').toJSDate() : null;
    this._show = this.getAttribute("show") ? DateTime.fromFormat(this.getAttribute("show") + " America/Los_Angeles", 'yyyy-MM-dd HH:mm:ss z').toJSDate() : null;

    this._teaserGroup = this.getAttribute("teaser-group") || "";
    this._doAutoOpen = this.getAttribute("auto-open") !== "false";
    this._teasers = [];
    this._teaserCloseEls = [];
    this._teaserCds = [];
    this._teaserCdInterval = null;
    this._openTriggers = [];
    this._scrollReveal = null;
    this._lastFocused = null;
    this._closeEls = this.querySelectorAll("[data-apnp-close]");
    this._days = this.querySelector(".days");
    this._hours = this.querySelector(".hours");
    this._minutes = this.querySelector(".minutes");
    this._seconds = this.querySelector(".seconds");

    // countdown targets the Countdown block's own datetime (independent of the popup's active window)
    var cdEl = this.querySelector(".apnp__countdown");
    var cdEnd = cdEl ? cdEl.getAttribute("data-countdown-end") : "";
    this._cdEnd = cdEnd ? DateTime.fromFormat(cdEnd + " America/Los_Angeles", 'yyyy-MM-dd HH:mm:ss z').toJSDate() : null;

    this._onClose = this._close.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onTeaserActivate = this._teaserActivate.bind(this);
    this._onTeaserDismiss = this._dismissTeaser.bind(this);
    this._onOpenTrigger = this._openFromTrigger.bind(this);
  }
  get _designMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }
  connectedCallback() {
    this._closeEls.forEach((el) => el.addEventListener("click", this._onClose));

    if (this._teaserGroup) {
      this._teasers = Array.prototype.slice.call(document.querySelectorAll('[data-apnp-teaser="' + this._teaserGroup + '"]'));
      this._teasers.forEach((t) => {
        t.addEventListener("click", this._onTeaserActivate);
        const closeEl = t.querySelector("[data-apnp-teaser-close]");
        if (closeEl) { this._teaserCloseEls.push(closeEl); closeEl.addEventListener("click", this._onTeaserDismiss); }
        // circle teaser countdown (adaptive hrs/min/sec; hides itself when it ends so only the discount text remains)
        const cdEl = t.querySelector("[data-teaser-cd-end]");
        if (cdEl) {
          const endStr = cdEl.getAttribute("data-teaser-cd-end");
          const dt = endStr ? DateTime.fromFormat(endStr + " America/Los_Angeles", "yyyy-MM-dd HH:mm:ss z").toJSDate() : null;
          if (dt && !isNaN(dt.getTime())) {
            this._teaserCds.push({
              cdEl: cdEl,
              ctextEl: t.querySelector(".apnp-teaser__ctext"),
              ringEl: t.querySelector(".apnp-teaser__ring"),
              ended: false,
              num: cdEl.querySelector(".cd-num"),
              unit: cdEl.querySelector(".cd-unit"),
              end: dt,
              uh: cdEl.getAttribute("data-cd-h") || "hrs",
              um: cdEl.getAttribute("data-cd-m") || "min",
              us: cdEl.getAttribute("data-cd-s") || "sec"
            });
          }
        }
      });
      if (this._teaserCds.length) { this._teaserCdTick(true); this._teaserCdInterval = setInterval(() => this._teaserCdTick(false), 1000); }
    }
    // any element with [data-apnp-open] opens the popup (manual trigger)
    this._openTriggers = Array.prototype.slice.call(document.querySelectorAll("[data-apnp-open]"));
    this._openTriggers.forEach((el) => el.addEventListener("click", this._onOpenTrigger));

    if (this._designMode) {
      this.removeAttribute("hidden");
      this._showTeaser(); // keep teasers visible in the editor so they can be selected/edited
      if (this._doAutoOpen) this._openTimer = setTimeout(this._open.bind(this), 400);
      return;
    }

    const now = Date.now();
    if (this._isValidDate(this._show) && now < this._show.getTime()) return; // not started
    if (this._isValidDate(this._end) && now >= this._end.getTime()) return;  // already ended

    if (!this._doAutoOpen) {
      // popup never opens by itself: reveal the teaser(s) as a standalone entry point on first scroll
      if (!this._teaserDismissed()) this._showTeaserOnScroll();
      return;
    }

    if (this._freqDays > 0 && this._recentlyShown(now)) {
      // already shown this cycle: reveal the teaser on first scroll (not immediately on a page load/refresh)
      if (!this._teaserDismissed()) this._showTeaserOnScroll();
      return;
    }

    this.removeAttribute("hidden");
    this._openTimer = setTimeout(this._autoOpen.bind(this), this._delay * 1e3);
  }
  _openFromTrigger(event) {
    if (event) event.preventDefault();
    this._open();
  }
  _teaserActivate(event) {
    const el = event.currentTarget;
    if (el.getAttribute("data-apnp-teaser-action") === "link") {
      const link = el.getAttribute("data-apnp-teaser-link");
      if (link) window.location.href = link;
      return;
    }
    this._open();
  }
  _autoOpen() {
    this._remember();
    // new frequency cycle: allow the teaser to appear again even if it was dismissed last cycle
    try { window.localStorage.removeItem(this._teaserDismissKey); } catch (e) {}
    this._open();
  }
  _open() {
    if (this._opened || !this.isConnected) return;
    this._opened = true;
    this._lastFocused = document.activeElement;
    this.removeAttribute("hidden");
    this._restartMediaAnimations();
    if (!this._designMode) this._hideTeaser(); // in the editor keep teasers visible alongside the popup

    if (this._isValidDate(this._cdEnd) && this._days) {
      this._interval = setInterval(this._tick.bind(this), 1e3);
      this._tick();
    }

    this._prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => this.classList.add("is-open"));
    document.addEventListener("keydown", this._onKeydown);

    const focusTarget = this.querySelector(".apnp__close");
    if (focusTarget) focusTarget.focus();
  }
  _close(event) {
    if (event) event.preventDefault();
    if (!this._opened) return;
    this._opened = false;
    this.classList.remove("is-open");
    document.body.style.overflow = this._prevOverflow || "";
    document.removeEventListener("keydown", this._onKeydown);
    this._interval && clearInterval(this._interval);

    if (!this._teaserDismissed()) this._showTeaser();
    if (this._lastFocused && typeof this._lastFocused.focus === "function") this._lastFocused.focus();

    // hide (display:none) once the closing transition finishes, unless reopened meanwhile, so a closed popup costs
    // nothing; animated SVGs are restarted in _open so re-opening still shows motion immediately.
    this.addEventListener("transitionend", () => { if (!this._opened) this.setAttribute("hidden", ""); }, { once: true });
  }
  _onKeydown(event) {
    if (!this._opened) return;
    if (event.key === "Escape" || event.key === "Esc") {
      this._close();
      return;
    }
    if (event.key === "Tab") {
      const f = this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }
  _restartMediaAnimations() {
    // While closed the popup is display:none, which pauses SVG animations; re-inserting each content SVG restarts its
    // CSS/SMIL animations so motion is visible immediately on (re)open instead of after a delay.
    this.querySelectorAll(".apnp__media svg, .apnp__title-icon svg").forEach((svg) => {
      svg.replaceWith(svg.cloneNode(true));
    });
  }
  _teaserCdTick(initial) {
    const now = Date.now();
    this._teaserCds.forEach((o) => {
      const remain = o.end.getTime() - now;
      if (remain <= 0) {
        if (o.ended) return;
        o.ended = true;
        // countdown ended -> hide the countdown line and re-center the discount text (drop the with-countdown offset)
        if (o.cdEl) o.cdEl.setAttribute("hidden", "");
        if (o.ctextEl) o.ctextEl.classList.remove("apnp-teaser__ctext--with-cd");
        // the ring only exists alongside the countdown: let it finish the current loop before vanishing (never cut it
        // off mid-spin). Drop it right away when there is no live spin to finish: at page load (already over), or when
        // the animation is off (prefers-reduced-motion) so animationiteration would never fire.
        if (o.ringEl) {
          const arc = o.ringEl.querySelector(".apnp-teaser__ring-arc");
          const spinning = !initial && arc && getComputedStyle(arc).animationName !== "none";
          if (spinning) {
            arc.addEventListener("animationiteration", () => o.ringEl.setAttribute("hidden", ""), { once: true });
          } else {
            o.ringEl.setAttribute("hidden", "");
          }
        }
        return;
      }
      const s = Math.floor(remain / 1000);
      let num, unit;
      if (s >= 3600) { num = Math.floor(s / 3600); unit = o.uh; }
      else if (s >= 60) { num = Math.floor(s / 60); unit = o.um; }
      else { num = s; unit = o.us; }
      if (o.num) o.num.textContent = num;
      if (o.unit) o.unit.textContent = unit;
    });
    // nothing left to tick once every countdown has ended
    if (this._teaserCdInterval && this._teaserCds.every((o) => o.ended)) {
      clearInterval(this._teaserCdInterval);
      this._teaserCdInterval = null;
    }
  }
  _showTeaserOnScroll() {
    // reveal after the visitor has scrolled ~5% of the page (min 40px; not on the very first pixel)
    if (this._scrollReveal) return;
    const events = ["scroll", "wheel", "touchmove"];
    const check = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const threshold = Math.max(40, maxScroll * 0.05);
      if (window.scrollY >= threshold || maxScroll < 80) {
        events.forEach((e) => window.removeEventListener(e, check));
        this._scrollReveal = null;
        this._showTeaser();
      }
    };
    this._scrollReveal = { events: events, reveal: check };
    events.forEach((e) => window.addEventListener(e, check, { passive: true }));
  }
  _showTeaser() {
    if (this._scrollReveal) { this._scrollReveal.events.forEach((e) => window.removeEventListener(e, this._scrollReveal.reveal)); this._scrollReveal = null; }
    this._teasers.forEach((t) => { t.removeAttribute("hidden"); t.classList.add("is-visible"); });
    // restart any animated SVG background inside a circle teaser (paused while it was display:none)
    this._teasers.forEach((t) => {
      t.querySelectorAll(".apnp-teaser__cbg svg").forEach((svg) => svg.replaceWith(svg.cloneNode(true)));
    });
  }
  _hideTeaser() {
    this._teasers.forEach((t) => { t.classList.remove("is-visible"); t.setAttribute("hidden", ""); });
  }
  _dismissTeaser(event) {
    event.stopPropagation(); // don't trigger the teaser's re-open handler
    this._hideTeaser();
    if (!this._designMode) {
      // persist the dismissal so the teaser stays hidden across visits within this frequency window;
      // cleared in _autoOpen when the next cycle starts, so it never hides forever
      try { window.localStorage.setItem(this._teaserDismissKey, "1"); } catch (e) {}
    }
  }
  _teaserDismissed() {
    try { return window.localStorage.getItem(this._teaserDismissKey) === "1"; } catch (e) { return false; }
  }
  _recentlyShown(now) {
    let last = 0;
    try { last = parseInt(window.localStorage.getItem(this._key) || "0", 10); } catch (e) { last = 0; }
    return last > 0 && now - last < this._freqDays * 864e5;
  }
  _remember() {
    if (this._designMode) return;
    try { window.localStorage.setItem(this._key, String(Date.now())); } catch (e) {}
  }
  _tick() {
    const diff = this._cdEnd.getTime() - Date.now();
    if (diff <= 0) {
      this._setDigits(0, 0, 0, 0);
      this._interval && clearInterval(this._interval);
      return;
    }
    this._setDigits(
      Math.floor(diff / 864e5),
      Math.floor((diff % 864e5) / 36e5),
      Math.floor((diff % 36e5) / 6e4),
      Math.floor((diff % 6e4) / 1e3)
    );
  }
  _setDigits(days, hours, minutes, seconds) {
    const pad = (n) => (n < 10 ? "0" + n : "" + n);
    if (this._days) this._days.textContent = pad(days);
    if (this._hours) this._hours.textContent = pad(hours);
    if (this._minutes) this._minutes.textContent = pad(minutes);
    if (this._seconds) this._seconds.textContent = pad(seconds);
  }
  _isValidDate(d) {
    return d instanceof Date && !isNaN(d.getTime());
  }
  disconnectedCallback() {
    this._openTimer && clearTimeout(this._openTimer);
    this._interval && clearInterval(this._interval);
    this._teaserCdInterval && clearInterval(this._teaserCdInterval);
    document.removeEventListener("keydown", this._onKeydown);
    this._closeEls.forEach((el) => el.removeEventListener("click", this._onClose));
    this._teasers.forEach((t) => t.removeEventListener("click", this._onTeaserActivate));
    this._teaserCloseEls.forEach((el) => el.removeEventListener("click", this._onTeaserDismiss));
    this._openTriggers.forEach((el) => el.removeEventListener("click", this._onOpenTrigger));
    if (this._scrollReveal) { this._scrollReveal.events.forEach((e) => window.removeEventListener(e, this._scrollReveal.reveal)); this._scrollReveal = null; }
    if (this._opened) document.body.style.overflow = this._prevOverflow || "";
  }
};

if (!window.customElements.get("ap-newsletter-popup")) {
  window.customElements.define("ap-newsletter-popup", APNewsletterPopup);
}

// ---------------------------------------------------------------------------------------------------------------------
// MP PRODUCT CARD - "BUY NOW" GOES STRAIGHT TO CHECKOUT
// ---------------------------------------------------------------------------------------------------------------------
// The card's "Buy now" and cart-icon buttons are both type=submit inside the theme's <form is="product-form">, which
// always does an AJAX add-to-cart (drawer / cart page). For "Buy now" we instead add the item then redirect to the
// checkout. The cart-icon button keeps the theme's default behaviour. Delegated + capture so it pre-empts product-form.
document.addEventListener("submit", async (event) => {
  const submitter = event.submitter;
  if (!submitter || !submitter.classList.contains("mp-product-card__buttons-buy-now")) {
    return;
  }

  const form = event.target;
  event.preventDefault();
  event.stopPropagation(); // prevent the theme's product-form add-to-cart handler from also running

  if (submitter.hasAttribute("aria-busy")) {
    return;
  }
  submitter.setAttribute("aria-busy", "true");

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      body: new FormData(form)
    });

    if (response.ok) {
      window.location.href = `${root}checkout`;
      return;
    }

    const error = await response.json();
    form.dispatchEvent(new CustomEvent("cart:error", { bubbles: true, detail: { error: error.description } }));
  } catch (e) {
    // network error - re-enable the button below so the customer can retry
  }

  submitter.removeAttribute("aria-busy");
}, true);
